import { useMemo, useState } from "react";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import CarouselRail from "@/components/yts/CarouselRail";
import ExperienceCard from "@/components/yts/ExperienceCard";
import { StaticSection } from "@/components/yts/HomeSections";
import { useExplore, toCard } from "@/lib/ExploreContext";
import { groupExperiences } from "@/data/experiences";
import { useLang } from "@/lib/i18n";
import { categoryLabel } from "@/lib/locales";
import { VIATOR_SOURCE } from "@/lib/viator";

const RAIL_MAX = 9;
const INITIAL_THEMES = 6;
const moreOrLess = (es, expanded) => es
  ? expanded ? "Ver menos colecciones" : "Ver más colecciones"
  : expanded ? "Show fewer collections" : "Explore more collections";

/**
 * Editorial Experiences index: six considered collections initially, rather
 * than an endless supplier dump. Type and area use the universal quick-filters
 * immediately above; source and sorting stay secondary to choosing an activity.
 */
export default function ExperiencesCategoryView() {
  const { lang } = useLang();
  const es = lang === "es";
  const { filteredListings, loading, nbNames, clearFilters, experienceBucket, setExperienceBucket } = useExplore();
  const [sort, setSort] = useState("popular");
  const [provider, setProvider] = useState("all");
  const [visibleCount, setVisibleCount] = useState(18);
  const [expandedThemes, setExpandedThemes] = useState(false);

  const providers = useMemo(() => ({
    all: filteredListings.length,
    viator: filteredListings.filter((item) => item.source === VIATOR_SOURCE).length,
    fareharbor: filteredListings.filter((item) => item.affiliate_provider === "FareHarbor").length,
  }), [filteredListings]);
  const partnerListings = useMemo(() => filteredListings.filter((item) => (
    provider === "all"
      || (provider === "viator" && item.source === VIATOR_SOURCE)
      || (provider === "fareharbor" && item.affiliate_provider === "FareHarbor")
  )), [filteredListings, provider]);

  const groups = useMemo(() => groupExperiences(partnerListings, sort), [partnerListings, sort]);
  const selected = experienceBucket === "All" ? null : groups.find((group) => group.title === experienceBucket);
  const visibleGroups = experienceBucket === "All"
    ? (expandedThemes ? groups : groups.slice(0, INITIAL_THEMES))
    : (selected ? [selected] : []);
  const total = groups.reduce((sum, group) => sum + group.items.length, 0);
  const extraThemes = Math.max(0, groups.length - INITIAL_THEMES);

  const cardFor = (item, label) => ({ ...toCard(item, nbNames), label });
  const showTheme = (title) => {
    setExperienceBucket(title);
    setVisibleCount(18);
    const heading = document.getElementById("category-results");
    heading?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) return (
    <div id="category-results" className="mx-auto max-w-[1240px] px-4 py-10 text-[13px] text-[#777]">
      {es ? "Cargando experiencias…" : "Loading experiences…"}
    </div>
  );

  return (
    <div className="bg-white pb-12 text-[#171717] dark:bg-[#171719] dark:text-white">
      <StaticSection id="category-results">
        <div className="border-t-2 border-[#171717] pt-4 dark:border-white">
          <p className="text-[10px] font-bold uppercase tracking-[.12em] text-[#c43c58] dark:text-[#ff9cb9]">
            {es ? "YA TÚ SABES PR / EXPERIENCIAS" : "YA TÚ SABES PR / EXPERIENCES"}
          </p>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-[clamp(29px,5.8vw,55px)] font-extrabold leading-[1.04] tracking-[-.034em]">
                {es ? "Haz algo memorable." : "Do something memorable."}
              </h1>
              <p className="mt-2 max-w-[55ch] text-[13px] leading-[1.6] text-[#63615c] dark:text-white/65 sm:text-[14px]">
                {es
                  ? "Cultura, agua, aventura y música. Explora una colección o elige tipo y área arriba."
                  : "Culture, water, adventure and music. Explore a collection, or choose a type and area above."}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="relative inline-flex items-center gap-1.5">
                <span className="sr-only">{es ? "Ordenar experiencias" : "Sort experiences"}</span>
                <select
                  value={sort}
                  onChange={(event) => setSort(event.target.value)}
                  className="h-10 min-w-[132px] appearance-none rounded-md border border-black/15 bg-white pl-3 pr-8 text-[12px] font-semibold text-[#171717] outline-none focus-visible:ring-2 focus-visible:ring-[#e11d48] dark:border-white/25 dark:bg-[#242326] dark:text-white"
                >
                  <option value="popular">{es ? "Recomendadas" : "Recommended"}</option>
                  <option value="rating">{es ? "Mejor valoradas" : "Top rated"}</option>
                  <option value="price">{es ? "Precio: menor a mayor" : "Price: low to high"}</option>
                </select>
                <ChevronDown size={15} className="pointer-events-none absolute right-2.5 text-[#777]" aria-hidden="true"/>
              </label>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-black/[0.08] pt-3 dark:border-white/10">
            <span className="mr-1 text-[10px] font-bold uppercase tracking-[.08em] text-[#8a8580]">
              {es ? "Proveedor" : "Provider"}
            </span>
            {[
              { id:"all",en:"All experiences",es:"Todas" },
              { id:"viator",en:"Viator",es:"Viator" },
              { id:"fareharbor",en:"FareHarbor",es:"FareHarbor" }
            ].map(({id,en,es:labelEs}) => (
              <button key={id} type="button" aria-pressed={provider===id}
                onClick={() => {setProvider(id);setVisibleCount(18);setExpandedThemes(false);}}
                className={`min-h-9 rounded-full border px-3 text-[11px] font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-[#e11d48] ${provider===id
                  ? "border-[#171717] bg-[#171717] text-white dark:border-white dark:bg-white dark:text-black"
                  : "border-black/[0.12] bg-transparent text-[#555] hover:border-black/50 dark:border-white/25 dark:text-white/75"}`}
              >{es ? labelEs : en}<span className="ml-1.5 opacity-60">{providers[id].toLocaleString()}</span></button>
            ))}
          </div>
        </div>
      </StaticSection>

      {total === 0 || (experienceBucket !== "All" && !selected) ? (
        <div className="mx-auto max-w-[1240px] px-4 py-12 text-center sm:px-6">
          <p className="text-[16px] font-semibold">{es ? "No encontramos experiencias con esos filtros." : "No experiences match those choices yet."}</p>
          <p className="mt-1 text-[12px] text-[#777]">{es ? "Prueba otro tipo, área o socio." : "Try a different type, area or booking partner."}</p>
          <button type="button" className="mt-4 min-h-10 rounded-md border border-[#171717] px-4 text-[12px] font-bold" onClick={() => {clearFilters();setProvider("all");setExperienceBucket("All");}}>
            {es ? "Ver todas" : "Clear filters"}
          </button>
        </div>
      ) : (
        <>
          {visibleGroups.map((group) => {
            const focused = experienceBucket === group.title;
            const limit = focused ? visibleCount : RAIL_MAX;
            const items = group.items.slice(0, limit);
            return (
              <CarouselRail
                key={group.title}
                editorial
                title={categoryLabel(group.title, lang)}
                subtitle={es
                  ? `${group.items.length} opciones para explorar.`
                  : `${group.items.length} experiences to explore.`}
                action={!focused && group.items.length > RAIL_MAX ? (
                  <button type="button" onClick={() => showTheme(group.title)}
                    className="inline-flex min-h-10 items-center gap-1 text-[11px] font-bold underline underline-offset-4 hover:opacity-70">
                    {es ? "Ver la colección" : "View collection"} <ArrowUpRight size={15}/>
                  </button>
                ) : focused ? (
                  <button type="button" onClick={() => {setExperienceBucket("All");setVisibleCount(18);}}
                    className="inline-flex min-h-10 items-center gap-1 text-[11px] font-bold underline underline-offset-4">
                    {es ? "Todas las colecciones" : "All collections"}
                  </button>
                ) : null}
              >
                {items.map((item) => <ExperienceCard key={item.id} item={cardFor(item, group.title)} />)}
              </CarouselRail>
            );
          })}
          {experienceBucket === "All" && extraThemes > 0 && (
            <div className="mx-auto max-w-[1240px] px-4 py-5 text-center sm:px-6">
              <button type="button" onClick={() => setExpandedThemes((x) => !x)}
                className="inline-flex min-h-11 items-center gap-2 rounded-md border border-black/20 px-5 text-[12px] font-bold transition hover:border-black dark:border-white/30 dark:hover:border-white">
                {moreOrLess(es, expandedThemes)}{!expandedThemes && <span className="text-[#888]">+{extraThemes}</span>}
                <ChevronDown size={16} className={expandedThemes ? "rotate-180" : ""}/>
              </button>
            </div>
          )}
          {selected && selected.items.length > visibleCount && (
            <div className="mx-auto max-w-[1240px] px-4 py-5 text-center sm:px-6">
              <button type="button" onClick={() => setVisibleCount((n) => n + 18)}
                className="min-h-11 rounded-md border border-black/20 px-5 text-[12px] font-bold dark:border-white/30">
                {es ? "Ver más experiencias" : "Load more experiences"}
              </button>
            </div>
          )}
        </>
      )}
      <div className="mx-auto max-w-[1240px] px-4 pt-5 text-[11px] leading-relaxed text-[#77736e] dark:text-white/60 sm:px-6">
        {es
          ? "Fechas, tarifas y pagos son gestionados por el proveedor de cada experiencia. Confirma los detalles antes de reservar."
          : "Dates, prices and payment are handled by each experience provider. Confirm details before booking."}
      </div>
    </div>
  );
}
